import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import MobileChrome from './components/MobileChrome';
import { ReminderBanner } from './components/Reminders';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DesktopPages from './pages/DesktopPages';
import JobDetail from './pages/JobDetail';
import AskAIRoute from './pages/AskAIRoute';
import ChiefOfStaffPage from './pages/ChiefOfStaffPage';
import Podcasts from './pages/Podcasts';
import DailyUpdates from './pages/DailyUpdates';
import MyAsks from './pages/MyAsks';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminEmployees from './pages/admin/AdminEmployees';
import AdminEmployeeHub from './pages/admin/AdminEmployeeHub';
import AdminTimeOff from './pages/admin/AdminTimeOff';
import AdminCertifications from './pages/admin/AdminCertifications';
import AdminHoleDetail from './pages/admin/AdminHoleDetail';
import AdminSupplierUpdates from './pages/admin/AdminSupplierUpdates';
import AdminAsks from './pages/admin/AdminAsks';
import AdminProjectDetail from './pages/admin/AdminProjectDetail';
import AdminPurchaseOrders from './pages/admin/AdminPurchaseOrders';
import AdminConversations from './pages/admin/AdminConversations';
import AdminTimesheets from './pages/admin/AdminTimesheets';
import AdminSafety from './pages/admin/AdminSafetyHub';   // the Safety page (Training / Documents / JHAs) — its own menu item since Sep 2026
import AdminVendors from './pages/admin/AdminVendors';
import AdminBids from './pages/admin/AdminBids';
import AdminBidDetail, { AdminBidDetailInner } from './pages/admin/AdminBidDetail';
import AdminJobDetail from './pages/admin/AdminJobDetail';
import AdminClients from './pages/admin/AdminClients';
import AdminNewProjects from './pages/admin/AdminNewProjects';
import AdminNewProjectDetail from './pages/admin/AdminNewProjectDetail';
import AdminWorkflows from './pages/admin/AdminWorkflows';
import AdminSettings from './pages/admin/AdminSettings';
import AdminMaterials from './pages/admin/AdminMaterials';
import AdminCategories from './pages/admin/AdminCategories';
import AdminChecklists from './pages/admin/AdminChecklists';
import AdminRateSheet from './pages/admin/AdminRateSheet';
import AdminBidAssemblies from './pages/admin/AdminBidAssemblies';
import AdminTrenching from './pages/admin/AdminTrenching';
import AdminPriceBook from './pages/admin/AdminPriceBook';
import AdminDirectives from './pages/admin/AdminDirectives';
import MyPriorities from './pages/admin/MyPriorities';
import AdminAskAIRoute from './pages/admin/AdminAskAIRoute';
import AdminChiefOfStaff from './pages/admin/AdminChiefOfStaff';
import AdminStock from './pages/admin/AdminStock';
import AdminEquipment from './pages/admin/AdminEquipment';
import AdminAdministration from './pages/admin/AdminAdministration';
import AdminInvoiceLog from './pages/admin/AdminInvoiceLog';
import WoBilling from './pages/admin/WoBilling';
import LargeJobBilling from './pages/admin/LargeJobBilling';
import AdminCardCharges from './pages/admin/AdminCardCharges';
import AdminPermits from './pages/admin/AdminPermits';
import AdminShop from './pages/admin/AdminShop';
import MobilePreview from './pages/admin/MobilePreview';
import DesktopPreview from './pages/admin/DesktopPreview';
import ViewAsUser from './pages/admin/ViewAsUser';
import PmGlance from './pages/admin/PmGlance';
import AdminSchedule from './pages/admin/AdminSchedule';
import WorkflowSchedulePage from './pages/admin/WorkflowSchedulePage';
import PMDashboard from './pages/portal/PMDashboard';
import CompanyConversations from './pages/portal/CompanyConversations';
import WorkerProjectDetail from './pages/WorkerProjectDetail';
import SafetyTraining from './pages/SafetyTraining';
import SafetyDocs from './pages/SafetyDocs';
import SupShop from './pages/SupShop';
import SupBids from './pages/SupBids';
import SupProject from './pages/SupProject';
import MyUpdates from './pages/MyUpdates';
import ApprovedSubmittals from './pages/ApprovedSubmittals';
import CreatePO from './pages/CreatePO';
import ShopPos from './pages/ShopPos';
import Directives from './pages/Directives';
import ProjectWorkflows from './pages/ProjectWorkflows';
import UsaDigsScreen from './pages/UsaDigsScreen';
import TimeTracking from './pages/TimeTracking';
import PretripInspection from './pages/PretripInspection';
import ReportAccident from './pages/ReportAccident';
import ShopRequest from './pages/ShopRequest';
import JhaField from './pages/JhaField';
import './App.css';

function ProtectedWorker({ children }) {
  const { worker } = useAuth();
  return worker ? children : <Navigate to="/" replace />;
}

const STAFF_ROLES = ['OfficeStaff', 'Accounting', 'ProjectManager', 'Executive'];

// Jobs and workflow boards exist twice — an office route and the field's own copy of
// the same screen. A pointer is sent by whoever was looking at it, so it carries the
// sender's route; landing a supervisor on the office one used to bounce them to the
// home screen with no idea what they had been sent. Send them to their own copy.
const FIELD_TWIN = [
  [/^\/admin\/jobs\/(.+)$/, '/sup/jobs/$1'],
  [/^\/admin\/new-projects\/([^/]+)$/, '/sup/jobs/$1'],
  [/^\/admin\/workflows(\/.*)?$/, '/sup/workflows$1'],
  // The shop's own people live on /sup/shop, not the office /admin/shop. A shop-order
  // notification (Mike's most common) pointed at the office page, which bounced him
  // home; send him to his copy, query string and all (the order to open rides along).
  [/^\/admin\/shop$/, '/sup/shop'],
  [/^\/admin\/directives$/, '/directives'],
];

function ProtectedAdmin({ children }) {
  try {
    const session = JSON.parse(localStorage.getItem('titan_session') || 'null');
    if (session?.role === 'admin' || STAFF_ROLES.includes(session?.role)) return children;
    const { pathname, search } = window.location;
    for (const [re, to] of FIELD_TWIN) {
      if (re.test(pathname)) return <Navigate to={pathname.replace(re, to) + search} replace />;
    }
  } catch {}
  return <Navigate to="/" replace />;
}

// The preview tools render the app AS somebody else, so they're owner/Executive only.
// ProtectedAdmin lets in every staff role, which would leave the pages reachable by
// URL even with the menu entry hidden.
// The Executive hub for the owner and the Executives; anyone else who follows a link
// there (an old bell link, a bookmark) lands on their own priorities instead.
function DirectivesOrPriorities() {
  try {
    const session = JSON.parse(localStorage.getItem('titan_session') || 'null');
    if (session?.role === 'admin' || session?.role === 'Executive') return <AdminDirectives />;
  } catch {}
  return <Navigate to={'/admin/priorities' + window.location.search} replace />;
}
function ProtectedExec({ children }) {
  try {
    const session = JSON.parse(localStorage.getItem('titan_session') || 'null');
    if (session?.role === 'admin' || session?.role === 'Executive') return children;
  } catch {}
  return <Navigate to="/admin" replace />;
}

// The Employees area (list, time off, whatever tiles come next) is for the owner,
// Executives and the office (role 'Accounting') — not PMs.
function ProtectedOffice({ children }) {
  try {
    const session = JSON.parse(localStorage.getItem('titan_session') || 'null');
    if (['admin', 'Executive', 'Accounting'].includes(session?.role)) return children;
  } catch {}
  return <Navigate to="/admin" replace />;
}

function AppRoutes() {
  const { worker } = useAuth();
  // Tapping the left-menu item for the page you're already on starts that page over — back to its
  // main tiles (Pat, Oct 9 2026: "on an Executive tile, the Executive bookmark should take you back").
  // AdminLayout sends { navReset } in the location state; the pages below remount only when it changes.
  const location = useLocation();
  const navResetRef = React.useRef(0);
  if (location.state && location.state.navReset && location.state.navReset !== navResetRef.current) navResetRef.current = location.state.navReset;
  let portalRole = null;
  try {
    const session = JSON.parse(localStorage.getItem('titan_session') || 'null');
    portalRole = session?.role || null;
  } catch {}
  if (portalRole === 'admin' && window.location.pathname === '/') return <Navigate to="/admin" replace />;
  if (STAFF_ROLES.includes(portalRole) && window.location.pathname === '/') {
    const landingMap = { ProjectManager: '/dashboard', Accounting: '/admin', OfficeStaff: '/admin/new-projects', Executive: '/admin' };
    return <Navigate to={landingMap[portalRole] || '/admin/new-projects'} replace />;
  }
  return (
    <Routes key={navResetRef.current}>
      <Route path="/" element={<Login />} />
      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
      {/* Trello is not used (Oct 6 2026) — its old page sends you to the Dashboard. */}
      <Route path="/admin/trello" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/employees" element={<ProtectedOffice><AdminEmployeeHub /></ProtectedOffice>} />
      <Route path="/admin/employees/list" element={<ProtectedOffice><AdminEmployees /></ProtectedOffice>} />
      <Route path="/admin/send-asks" element={<ProtectedAdmin><AdminAsks /></ProtectedAdmin>} />
      <Route path="/admin/employees/time-off" element={<ProtectedOffice><AdminTimeOff /></ProtectedOffice>} />
      {/* Certifications (Pat, Oct 5 2026): Office/Exec manage; PMs get the status-only grid. */}
      <Route path="/admin/employees/certifications" element={<ProtectedAdmin><AdminCertifications /></ProtectedAdmin>} />
      {/* Where an employee's card bells land: the phone home with My Cards open. */}
      <Route path="/my-cards" element={<ProtectedWorker><Navigate to="/dashboard?cards=1" replace /></ProtectedWorker>} />
      <Route path="/admin/holes/:holeId" element={<ProtectedAdmin><AdminHoleDetail /></ProtectedAdmin>} />
      <Route path="/admin/wedco-updates" element={<ProtectedAdmin><AdminSupplierUpdates /></ProtectedAdmin>} />
      <Route path="/admin/projects/:id" element={<ProtectedAdmin><AdminProjectDetail /></ProtectedAdmin>} />
      <Route path="/admin/purchase-orders" element={<ProtectedAdmin><AdminPurchaseOrders /></ProtectedAdmin>} />
      <Route path="/admin/invoices" element={<ProtectedAdmin><AdminInvoiceLog /></ProtectedAdmin>} />
      <Route path="/admin/conversations" element={<ProtectedAdmin><AdminConversations /></ProtectedAdmin>} />
      <Route path="/admin/timesheets" element={<ProtectedAdmin><AdminTimesheets /></ProtectedAdmin>} />
      <Route path="/admin/safety" element={<ProtectedAdmin><AdminSafety /></ProtectedAdmin>} />
      <Route path="/admin/vendors" element={<ProtectedAdmin><AdminVendors /></ProtectedAdmin>} />
      <Route path="/admin/bids" element={<ProtectedAdmin><AdminBids /></ProtectedAdmin>} />
      <Route path="/admin/bids/:id" element={<ProtectedAdmin><AdminBidDetail /></ProtectedAdmin>} />
      {/* Rebuilt job page — five tiles. Runs alongside /admin/bids/:id while the layout is settled. */}
      <Route path="/admin/jobs/:id" element={<ProtectedAdmin><AdminJobDetail /></ProtectedAdmin>} />
      <Route path="/admin/jobs/:id/:tile" element={<ProtectedAdmin><AdminJobDetail /></ProtectedAdmin>} />
      <Route path="/admin/new-projects" element={<ProtectedAdmin><AdminNewProjects /></ProtectedAdmin>} />
      {/* The new tiled page is what a job opens as. The old one keeps its own
          address so anyone can still get to it — see the button on the new page. */}
      <Route path="/admin/new-projects/:id" element={<ProtectedAdmin><AdminJobDetail /></ProtectedAdmin>} />

      <Route path="/admin/old-job/:id" element={<ProtectedAdmin><AdminNewProjectDetail /></ProtectedAdmin>} />
      <Route path="/admin/workflows" element={<ProtectedAdmin><AdminWorkflows /></ProtectedAdmin>} />
      <Route path="/admin/workflows/:type" element={<ProtectedAdmin><AdminWorkflows /></ProtectedAdmin>} />
      <Route path="/admin/workflows/:type/schedule" element={<ProtectedAdmin><WorkflowSchedulePage /></ProtectedAdmin>} />
      <Route path="/admin/settings" element={<ProtectedAdmin><AdminSettings /></ProtectedAdmin>} />
      <Route path="/admin/materials" element={<ProtectedAdmin><AdminMaterials /></ProtectedAdmin>} />
      <Route path="/admin/categories" element={<ProtectedAdmin><AdminCategories /></ProtectedAdmin>} />
      <Route path="/admin/checklists" element={<ProtectedAdmin><AdminChecklists /></ProtectedAdmin>} />
      <Route path="/admin/rate-sheet" element={<ProtectedAdmin><AdminRateSheet /></ProtectedAdmin>} />
      <Route path="/admin/bid-assemblies" element={<ProtectedAdmin><AdminBidAssemblies /></ProtectedAdmin>} />
      <Route path="/admin/trenching" element={<ProtectedAdmin><AdminTrenching /></ProtectedAdmin>} />
      <Route path="/admin/price-book" element={<ProtectedAdmin><AdminPriceBook /></ProtectedAdmin>} />
      {/* The Executive hub is the owner's and the Executives' (Ryan, Sep 30 2026); everyone else's priorities live at /admin/priorities. */}
      <Route path="/admin/directives" element={<ProtectedAdmin><DirectivesOrPriorities /></ProtectedAdmin>} />
      <Route path="/admin/priorities" element={<ProtectedAdmin><MyPriorities /></ProtectedAdmin>} />
      <Route path="/admin/stock" element={<ProtectedAdmin><AdminStock /></ProtectedAdmin>} />
      <Route path="/admin/equipment" element={<ProtectedAdmin><AdminEquipment /></ProtectedAdmin>} />
      <Route path="/admin/administration" element={<ProtectedAdmin><AdminAdministration /></ProtectedAdmin>} />
      <Route path="/admin/card-charges" element={<ProtectedAdmin><AdminCardCharges /></ProtectedAdmin>} />
      <Route path="/admin/wo-billing" element={<ProtectedAdmin><WoBilling /></ProtectedAdmin>} />
      <Route path="/admin/large-billing" element={<ProtectedAdmin><LargeJobBilling /></ProtectedAdmin>} />
      <Route path="/admin/permits" element={<ProtectedAdmin><AdminPermits /></ProtectedAdmin>} />
      <Route path="/admin/shop" element={<ProtectedAdmin><AdminShop /></ProtectedAdmin>} />
      <Route path="/admin/mobile-preview" element={<ProtectedExec><MobilePreview /></ProtectedExec>} />
      <Route path="/admin/desktop-preview" element={<ProtectedExec><DesktopPreview /></ProtectedExec>} />
      <Route path="/admin/view-as/:userId" element={<ProtectedAdmin><ViewAsUser /></ProtectedAdmin>} />
      <Route path="/admin/pm/:id" element={<ProtectedAdmin><PmGlance /></ProtectedAdmin>} />
      <Route path="/admin/schedule" element={<ProtectedAdmin><AdminSchedule /></ProtectedAdmin>} />
      <Route path="/admin/clients" element={<ProtectedAdmin><AdminClients /></ProtectedAdmin>} />
      <Route path="/admin/pm-dashboard" element={<ProtectedAdmin><PMDashboard /></ProtectedAdmin>} />
      <Route path="/admin/company-conversations" element={<ProtectedAdmin><CompanyConversations /></ProtectedAdmin>} />
      <Route path="/admin/ask" element={<ProtectedAdmin><AdminAskAIRoute /></ProtectedAdmin>} />
      <Route path="/admin/cos" element={<ProtectedAdmin><AdminChiefOfStaff /></ProtectedAdmin>} />
      {/* Worker routes */}
      <Route path="/dashboard" element={<ProtectedWorker><Dashboard /></ProtectedWorker>} />
      <Route path="/project/:id" element={<ProtectedWorker><WorkerProjectDetail /></ProtectedWorker>} />
      <Route path="/project/:id/workflows" element={<ProtectedWorker><ProjectWorkflows /></ProtectedWorker>} />
      <Route path="/create-po" element={<ProtectedWorker><CreatePO /></ProtectedWorker>} />
      <Route path="/shop/pos" element={<ProtectedWorker><ShopPos /></ProtectedWorker>} />
      <Route path="/directives" element={<ProtectedWorker><Directives /></ProtectedWorker>} />
      <Route path="/desktop-pages" element={<ProtectedWorker><DesktopPages /></ProtectedWorker>} />
      <Route path="/safety" element={<ProtectedWorker><SafetyTraining /></ProtectedWorker>} />
      <Route path="/safety-documents" element={<ProtectedWorker><SafetyDocs /></ProtectedWorker>} />
      <Route path="/sup/shop" element={<ProtectedWorker><SupShop /></ProtectedWorker>} />
      <Route path="/sup/workflows" element={<ProtectedWorker><AdminWorkflows embedded basePath="/sup/workflows" /></ProtectedWorker>} />
      <Route path="/sup/workflows/:type" element={<ProtectedWorker><AdminWorkflows embedded basePath="/sup/workflows" /></ProtectedWorker>} />
      <Route path="/sup/usa-digs/:sjId" element={<ProtectedWorker><UsaDigsScreen /></ProtectedWorker>} />
      <Route path="/sup/bids" element={<ProtectedWorker><SupBids /></ProtectedWorker>} />
      <Route path="/sup/bids/:id" element={<ProtectedWorker><AdminBidDetailInner embedded /></ProtectedWorker>} />
      {/* Field supervisor's view of the rebuilt job page — same five tiles, fewer pillows. */}
      <Route path="/sup/jobs/:id" element={<ProtectedWorker><AdminJobDetail embedded /></ProtectedWorker>} />
      <Route path="/sup/jobs/:id/:tile" element={<ProtectedWorker><AdminJobDetail embedded /></ProtectedWorker>} />
      {/* Same swap on the phone: the dashboard sends every job here. */}
      <Route path="/sup/project/:id" element={<ProtectedWorker><AdminJobDetail embedded /></ProtectedWorker>} />

      <Route path="/sup/old-job/:id" element={<ProtectedWorker><SupProject /></ProtectedWorker>} />
      <Route path="/sup/permits" element={<ProtectedWorker><AdminPermits embedded /></ProtectedWorker>} />
      <Route path="/sup/timesheets" element={<ProtectedWorker><AdminTimesheets embedded /></ProtectedWorker>} />
      <Route path="/sup/purchase-orders" element={<ProtectedWorker><AdminPurchaseOrders embedded /></ProtectedWorker>} />
      <Route path="/time" element={<ProtectedWorker><TimeTracking /></ProtectedWorker>} />
      <Route path="/pretrip" element={<ProtectedWorker><PretripInspection /></ProtectedWorker>} />
      <Route path="/report-accident" element={<ProtectedWorker><ReportAccident /></ProtectedWorker>} />
      <Route path="/shop-request" element={<ProtectedWorker><ShopRequest /></ProtectedWorker>} />
      <Route path="/jha" element={<ProtectedWorker><JhaField /></ProtectedWorker>} />
      <Route path="/job/:id" element={<ProtectedWorker><JobDetail /></ProtectedWorker>} />
      <Route path="/ask" element={<ProtectedWorker><AskAIRoute /></ProtectedWorker>} />
      <Route path="/cos" element={<ProtectedWorker><ChiefOfStaffPage /></ProtectedWorker>} />
      <Route path="/podcasts" element={<ProtectedWorker><Podcasts /></ProtectedWorker>} />
      <Route path="/daily" element={<ProtectedWorker><DailyUpdates /></ProtectedWorker>} />
      <Route path="/my-asks" element={<ProtectedWorker><MyAsks /></ProtectedWorker>} />
      <Route path="/my-updates" element={<ProtectedWorker><MyUpdates /></ProtectedWorker>} />
      <Route path="/sup/submittals" element={<ProtectedWorker><ApprovedSubmittals /></ProtectedWorker>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        {/* The bell and the send button follow the person around the phone app,
            because every mobile page builds its own header and would otherwise
            have neither. */}
        <MobileChrome />
        {/* A reminder that has come due sits across the top until Done or Snooze (Ryan, Oct 26 2026). */}
        <ReminderBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
